import { Icon } from "@templates/design-system";
import { getComponent } from "../../../../client";
import { VelvetMedia } from "../../../../modules/content/components/VelvetMedia/VelvetMedia";
import { VelvetRichText } from "../../../../modules/content/components/VelvetRichText/VelvetRichText";
import type { IFooterBlockProps } from "./FooterBlock.props";

export const FooterBlock = ({
  contact,
  copyright,
  description,
  logo,
  navigationGroups,
  parentClass,
  secondaryLinks,
  socialLinks,
}: IFooterBlockProps) => {
  if (
    !logo &&
    !description &&
    !contact?.email &&
    !contact?.phone &&
    !contact?.address &&
    !navigationGroups?.length &&
    !socialLinks?.length &&
    !secondaryLinks?.length &&
    !copyright
  )
    return null;
  const Link = getComponent("Link");
  const linkClass =
    "inline-flex min-h-11 items-center font-velvet-body text-base text-parchment/80 underline decoration-parchment/20 underline-offset-8 transition-colors hover:text-thread focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-thread";
  return (
    <footer
      className={`${parentClass || ""} border-t border-parchment/25 bg-velour text-parchment`}
    >
      <div className="velvet-container py-16 md:py-24">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            {logo ? (
              <VelvetMedia
                className="mb-10 h-24 w-full max-w-md bg-transparent"
                imageClassName="object-contain object-left"
                media={logo}
                sizes="192px"
              />
            ) : null}
            {description ? (
              <VelvetRichText
                className="max-w-sm text-base text-bone/80 md:text-lg"
                content={description}
              />
            ) : null}
            {contact ? (
              <address className="mt-5 flex flex-col not-italic">
                {contact.email ? (
                  <a className={linkClass} href={`mailto:${contact.email}`}>
                    {contact.email}
                  </a>
                ) : null}
                {contact.phone ? (
                  <a className={linkClass} href={`tel:${contact.phone}`}>
                    {contact.phone}
                  </a>
                ) : null}
                {contact.address ? (
                  <p className="mt-2 font-velvet-mono text-xs leading-6 text-bone/80">
                    {contact.address}
                  </p>
                ) : null}
              </address>
            ) : null}
          </div>
          {navigationGroups?.length ? (
            <nav
              aria-label="Footer"
              className="grid gap-10 border-t border-parchment/25 pt-6 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-3"
            >
              {navigationGroups.map((group, index) => (
                <div key={index}>
                  <h2 className="mb-4 font-velvet-display text-xl font-black uppercase leading-none text-thread">
                    {group.header}
                  </h2>
                  <ul>
                    {group.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          aria-label={link.accessibilityLabel || undefined}
                          className={linkClass}
                          href={link.href}
                          rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                          target={link.openInNewTab ? "_blank" : undefined}
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          ) : null}
          {socialLinks?.length ? (
            <div className="flex flex-col content-start border-t border-parchment/25 pt-6 lg:col-span-2">
              {socialLinks.map((item, index) => (
                <Link
                  aria-label={item.link.accessibilityLabel || undefined}
                  className="inline-flex min-h-11 items-center justify-between gap-3 border-b border-parchment/20 font-velvet-body text-base italic transition-colors hover:text-thread focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  href={item.link.href}
                  key={index}
                  rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={item.link.openInNewTab ? "_blank" : undefined}
                >
                  {item.icon ? (
                    <Icon aria-hidden="true" className="size-4" name={item.icon} />
                  ) : null}
                  {item.link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-16 flex flex-col-reverse gap-5 border-t border-parchment/30 pt-6 font-velvet-mono text-xs text-smoke sm:flex-row sm:items-center sm:justify-between">
          {secondaryLinks?.length ? (
            <div className="flex flex-wrap gap-x-5">
              {secondaryLinks.map((link, index) => (
                <Link
                  aria-label={link.accessibilityLabel || undefined}
                  className={linkClass}
                  href={link.href}
                  key={index}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  target={link.openInNewTab ? "_blank" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
          {copyright ? <p>{copyright}</p> : null}
        </div>
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
